import { cx } from '../cx.js';

/**
 * The shape of what is coming.
 *
 * Skeletons rather than spinners (§47), for one reason: a spinner says "wait",
 * a skeleton says "a table of six rows is about to appear here", and the
 * second stops the layout jumping when it does.
 *
 * `aria-hidden` throughout, with one live region saying what is loading. A
 * screen reader should hear "Chargement des appels", not eleven grey boxes.
 */
export function Skeleton({
  className,
  rounded = 'control',
}: {
  readonly className: string;
  readonly rounded?: 'control' | 'card' | 'pill';
}): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cx(
        'block animate-pulse bg-surface-muted',
        rounded === 'card'
          ? 'rounded-card'
          : rounded === 'pill'
            ? 'rounded-pill'
            : 'rounded-control',
        className,
      )}
    />
  );
}

/** A loading block that says out loud what it is loading. */
export function Loading({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

export function SkeletonRows({ rows = 5 }: { readonly rows?: number }): React.JSX.Element {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-14 w-full" />
      ))}
    </div>
  );
}

export function SkeletonMetrics({ count = 4 }: { readonly count?: number }): React.JSX.Element {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: count }, (_, index) => (
        <Skeleton key={index} className="h-32 w-full" rounded="card" />
      ))}
    </div>
  );
}
